import type { BookmarkNode, BrowserSettings, BrowserTab, HistoryEntry, InstalledExtension, OfflinePageRecord, PasswordRecord, TabGroup } from '../types';
import { DEFAULT_SETTINGS } from '../constants';
import { uid } from './ids';

const DB_NAME = 'websurf-db';
const DB_VERSION = 1;

type StoreName = 'keyval' | 'history' | 'offlinePages' | 'passwords' | 'extensions';

let dbPromise: Promise<IDBDatabase> | undefined;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  const promise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available in this environment.'));
      return;
    }
    let settled = false;
    const finish = (fn: () => void) => { if (!settled) { settled = true; fn(); } };
    const watchdog = setTimeout(() => finish(() => reject(new Error('IndexedDB open timed out.'))), 3000);
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onblocked = () => finish(() => { clearTimeout(watchdog); reject(new Error('IndexedDB open blocked by another tab.')); });
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('keyval')) db.createObjectStore('keyval');
      if (!db.objectStoreNames.contains('history')) {
        const history = db.createObjectStore('history', { keyPath: 'id' });
        history.createIndex('visitedAt', 'visitedAt');
        history.createIndex('url', 'url');
      }
      if (!db.objectStoreNames.contains('offlinePages')) {
        const offline = db.createObjectStore('offlinePages', { keyPath: 'id' });
        offline.createIndex('url', 'url', { unique: true });
        offline.createIndex('updatedAt', 'updatedAt');
      }
      if (!db.objectStoreNames.contains('passwords')) {
        const passwords = db.createObjectStore('passwords', { keyPath: 'id' });
        passwords.createIndex('origin', 'origin');
      }
      if (!db.objectStoreNames.contains('extensions')) db.createObjectStore('extensions', { keyPath: 'id' });
    };
    request.onsuccess = () => finish(() => { clearTimeout(watchdog); resolve(request.result); });
    request.onerror = () => finish(() => { clearTimeout(watchdog); reject(request.error || new Error('IndexedDB open failed.')); });
  }).catch((error) => {
    dbPromise = undefined;
    throw error;
  });
  dbPromise = promise;
  return promise;
}

async function tx<T>(storeName: StoreName, mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    let request: IDBRequest<T> | void;
    transaction.oncomplete = () => resolve(request ? request.result : undefined);
    transaction.onerror = () => reject(transaction.error || new Error(`Transaction failed for ${storeName}.`));
    request = work(store);
  });
}

async function getAll<T>(storeName: StoreName): Promise<T[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const request = db.transaction(storeName).objectStore(storeName).getAll();
    request.onsuccess = () => resolve(request.result as T[]);
    request.onerror = () => reject(request.error || new Error(`getAll failed for ${storeName}.`));
  });
}

export async function getKey<T>(key: string, fallback: T): Promise<T> {
  const value = await tx<T>('keyval', 'readonly', (store) => store.get(key));
  return value === undefined ? fallback : value;
}

export async function setKey<T>(key: string, value: T): Promise<void> {
  await tx('keyval', 'readwrite', (store) => store.put(value, key));
}

export async function loadSettings(): Promise<BrowserSettings> {
  const saved = await getKey<Partial<BrowserSettings>>('settings', {});
  return { ...DEFAULT_SETTINGS, ...saved };
}

export async function saveSettings(settings: BrowserSettings): Promise<void> {
  await setKey('settings', settings);
}

export async function loadTabs(): Promise<{ tabs: BrowserTab[]; activeTabId?: string; groups: TabGroup[] }> {
  return getKey('session', { tabs: [], groups: [] as TabGroup[] });
}

export async function saveTabs(session: { tabs: BrowserTab[]; activeTabId?: string; groups: TabGroup[] }): Promise<void> {
  await setKey('session', session);
}

export function defaultBookmarks(): BookmarkNode {
  const now = Date.now();
  return {
    id: 'root',
    type: 'folder',
    title: 'Bookmarks',
    createdAt: now,
    updatedAt: now,
    children: [
      { id: uid('bm'), type: 'bookmark', title: 'WebSurf Home', url: 'websurf://home', favicon: '/icons/mark-circle.png', createdAt: now, updatedAt: now },
      { id: uid('bm'), type: 'folder', title: 'Reading List', createdAt: now, updatedAt: now, children: [] }
    ]
  };
}

export async function loadBookmarks(): Promise<BookmarkNode> {
  return getKey('bookmarks', defaultBookmarks());
}

export async function saveBookmarks(root: BookmarkNode): Promise<void> {
  await setKey('bookmarks', root);
}

export async function addHistory(entry: Omit<HistoryEntry, 'id' | 'visitedAt'> & Partial<Pick<HistoryEntry, 'visitedAt'>>): Promise<HistoryEntry> {
  const record: HistoryEntry = { ...entry, id: uid('hist'), visitedAt: entry.visitedAt || Date.now() };
  await tx('history', 'readwrite', (store) => store.put(record));
  return record;
}

export async function listHistory(): Promise<HistoryEntry[]> {
  const items = await getAll<HistoryEntry>('history');
  return items.sort((a, b) => b.visitedAt - a.visitedAt);
}

export async function clearHistory(): Promise<void> {
  await tx('history', 'readwrite', (store) => store.clear());
}

export async function deleteHistory(id: string): Promise<void> {
  await tx('history', 'readwrite', (store) => store.delete(id));
}

export async function putOfflinePage(page: OfflinePageRecord): Promise<void> {
  await tx('offlinePages', 'readwrite', (store) => store.put(page));
}

export async function getOfflinePage(id: string): Promise<OfflinePageRecord | undefined> {
  return tx<OfflinePageRecord>('offlinePages', 'readonly', (store) => store.get(id));
}

export async function findOfflinePageByUrl(url: string): Promise<OfflinePageRecord | undefined> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const index = db.transaction('offlinePages').objectStore('offlinePages').index('url');
    const request = index.get(url);
    request.onsuccess = () => resolve(request.result as OfflinePageRecord | undefined);
    request.onerror = () => reject(request.error || new Error('Offline URL lookup failed.'));
  });
}

export async function listOfflinePages(): Promise<OfflinePageRecord[]> {
  const items = await getAll<OfflinePageRecord>('offlinePages');
  return items.sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function deleteOfflinePage(id: string): Promise<void> {
  await tx('offlinePages', 'readwrite', (store) => store.delete(id));
}

export async function putPassword(record: PasswordRecord): Promise<void> {
  await tx('passwords', 'readwrite', (store) => store.put(record));
}

export async function listPasswords(): Promise<PasswordRecord[]> {
  return getAll<PasswordRecord>('passwords');
}

export async function deletePassword(id: string): Promise<void> {
  await tx('passwords', 'readwrite', (store) => store.delete(id));
}

export async function putExtension(extension: InstalledExtension): Promise<void> {
  await tx('extensions', 'readwrite', (store) => store.put(extension));
}

export async function listExtensions(): Promise<InstalledExtension[]> {
  return getAll<InstalledExtension>('extensions');
}

export async function deleteExtension(id: string): Promise<void> {
  await tx('extensions', 'readwrite', (store) => store.delete(id));
}
